import { ReactElement, useCallback, useEffect, useMemo, useState } from 'react'
import { Box, IconButton, Typography } from '@mui/material'
import { ActionsDropdown } from '../../Components/ActionsDropdown'
import ConfirmationDialog from '../../../Common/ConfirmationDialog'
import {
    getAvailbleInsuranceActions,
    IInsuranceData,
    IPostInsuranceActionData,
    postInsuranceAction,
} from '../../../../services/entityServices'
import { useAppDispatch, useAppSelector } from '../../../../redux/hook'
import { findEntityById } from '../../../../utils/EntityUtils'
import {
    InsuranceAttributes,
    InsuranceStatus,
    publicShipmentView,
    ShipmentAttributes,
    TemplatePermissionMode,
    URLSearchParamsKeys,
    UserPermissions,
} from '../../../../constants/constants'
import { useTranslation } from 'react-i18next'
import AddorEditInsurance from './AddorEditInsurance'
import InsuranceViewItems from './InsuranceViewItems'
import InsuranceTopViewItems from './InsuranceTopViewItems'
import { useSearchParams } from 'react-router-dom'
import { updateLoader } from '../../../../redux/slices/loaderSlice'
import { fetchEntityInstanceData } from '../../../../services/entityInstanceServices'
import { IAttribute2 } from '../../../DTStudio/interfaces'

const defaultInsuracneData: IInsuranceData = {
    actions: [],
    attributes: [],
    isInsuranceCreated: false,
    insuranceEntityTemplateId: '',
    insuranceInstanceId: '',
}

const Insurance = (): ReactElement => {
    const { t } = useTranslation()

    const [searchParams] = useSearchParams()
    const dispatch = useAppDispatch()
    const permissions = useAppSelector((state) => state.org.permissions)

    const navSlice = useAppSelector((state) => state.nav)
    const isGuestUser =
        useAppSelector((state) => state.searchParams.isGuestUser) === 'true'
    const loggedInOrgId = useAppSelector((state) => state.org.org?.id)
    const publicSearchOrgId =
        useAppSelector((state) => state.searchParams.orgId) || ''

    const publicSearchViewId = useAppSelector(
        (state) => state.searchParams.instanceId,
    )

    const isPublicShipmentPreView =
        useAppSelector((state) => state.searchParams.publicShipmentView) ===
        publicShipmentView.SHIPMENT_PREVIEW
    const orgId = isPublicShipmentPreView
        ? publicSearchOrgId
        : loggedInOrgId || ''
    const shipmentInstanceId =
        (isPublicShipmentPreView
            ? publicSearchViewId
            : searchParams.get(URLSearchParamsKeys.instanceId)) || ''
    const entityViewData = useAppSelector(
        (state) => state.entityInstanceView.entityData,
    )
    const { selfEntityAttributeData, action } = entityViewData

    const [isOpenUserActionSubmit, setIsOpenUserActionSubmit] = useState(false)
    const [userActionValue, setUserActionValue] = useState('')
    const [insuranceData, setInsuranceData] =
        useState<IInsuranceData>(defaultInsuracneData)
    const [insuranceAttributesData, setInsuranceAttributesData] = useState<
        IAttribute2[]
    >([])
    const [readOnly, setReadOnly] = useState(true)

    const insuranceStatus = useMemo(() => {
        return insuranceData?.attributes?.find(
            (i) => i.id === InsuranceAttributes.InsuranceStatus,
        )?.value
    }, [insuranceData?.attributes])
    const insuranceActions = useMemo(
        () => insuranceData.actions || [],
        [insuranceData.actions],
    )
    const isInsuranceCreated = insuranceData.isInsuranceCreated || false
    const insuranceEntityTemplateId = useMemo(() => {
        return (
            selfEntityAttributeData.attributes?.find(
                (i) => i.id === ShipmentAttributes.CargoInsuranceAttributeId,
            )?.range || ''
        )
    }, [selfEntityAttributeData])
    const insuranceInstanceId = insuranceData.insuranceInstanceId || ''

    const insuranceEntityTemplateName = useMemo(
        () =>
            findEntityById(navSlice.navList, insuranceEntityTemplateId || '')
                .name ?? '',
        [navSlice.navList, insuranceEntityTemplateId],
    )

    const hasEditPermission =
        (action === TemplatePermissionMode.WRITE ||
            action === TemplatePermissionMode.READ_WRITE) &&
        permissions.includes(UserPermissions.ENTITY_WRITER) &&
        !isGuestUser

    const getInsuranceActions = useCallback(async () => {
        dispatch(updateLoader(true))
        const insuranceData = await getAvailbleInsuranceActions(
            shipmentInstanceId,
            orgId,
            isGuestUser,
        )
        const insuranceAttributesData = await fetchEntityInstanceData(
            orgId,
            insuranceEntityTemplateId,
            insuranceData?.insuranceInstanceId || '',
            isGuestUser,
        )
        setInsuranceAttributesData(
            insuranceAttributesData?.attributes as IAttribute2[],
        )
        setInsuranceData(insuranceData)
        setReadOnly(hasEditPermission ? insuranceData.isInsuranceCreated : true)
        dispatch(updateLoader(false))
    }, [
        orgId,
        hasEditPermission,
        isGuestUser,
        shipmentInstanceId,
        insuranceEntityTemplateId,
        dispatch,
    ])

    const onCloseUserActionsSubmit = useCallback(() => {
        setUserActionValue('')
        setIsOpenUserActionSubmit(false)
    }, [])

    const onSubmitUserActions = useCallback(async () => {
        dispatch(updateLoader(true))
        onCloseUserActionsSubmit()
        const insuranceActionData: IPostInsuranceActionData = {
            shipmentEntityId: shipmentInstanceId,
            action: userActionValue,
        }
        await postInsuranceAction(insuranceActionData, orgId)
        getInsuranceActions()
    }, [
        shipmentInstanceId,
        userActionValue,
        orgId,
        getInsuranceActions,
        onCloseUserActionsSubmit,
        dispatch,
    ])

    const onClickUserActions = useCallback((action: any) => {
        setUserActionValue(action.target.value)
        setIsOpenUserActionSubmit(true)
    }, [])

    const onClickEditInsurance = () => {
        setReadOnly(false)
    }

    const reload = () => {
        getInsuranceActions()
    }

    useEffect(() => {
        if (shipmentInstanceId && orgId) {
            getInsuranceActions()
        }
    }, [
        shipmentInstanceId,
        orgId,
        getInsuranceActions,
        hasEditPermission,
        isGuestUser,
    ])

    const canShowInsurance = hasEditPermission || Boolean(insuranceInstanceId)

    const canShowActions =
        hasEditPermission &&
        isInsuranceCreated &&
        insuranceStatus !== InsuranceStatus.ACCEPT &&
        insuranceActions?.length > 0

    return (
        <Box
            sx={{
                padding: '16px',
                display: 'flex',
                flexDirection: 'column',
                position: 'relative',
            }}
        >
            {!canShowInsurance && (
                <Typography
                    sx={{
                        fontStyle: 'italic',
                        textAlign: 'center',
                        color: 'rgba(0, 0, 0, 0.6)',
                        marginTop: '20px',
                        marginBottom: '20px',
                    }}
                >
                    {t('noInsuranceavailable')}
                </Typography>
            )}

            {canShowInsurance && (
                <>
                    {/* Actions */}
                    {canShowActions && (
                        <Box
                            sx={{
                                position: 'absolute',
                                right: '20px',
                                top: '10px',
                            }}
                        >
                            <Box
                                sx={{
                                    display: 'flex',
                                    justifyContent: 'flex-end',
                                }}
                            >
                                <IconButton
                                    id="edit-insurance-button"
                                    onClick={onClickEditInsurance}
                                    sx={{ marginRight: '10px' }}
                                >
                                    <img
                                        src="/assets/images/icons/edit.svg"
                                        alt="Edit"
                                    />
                                </IconButton>

                                <ActionsDropdown
                                    availableActions={insuranceActions}
                                    onClickUserActions={onClickUserActions}
                                    userActionValue={userActionValue}
                                />
                            </Box>
                        </Box>
                    )}

                    {/* Content */}

                    <Box>
                        {isInsuranceCreated ? (
                            <Box sx={{ marginTop: '10px', width: '100%' }}>
                                <InsuranceTopViewItems
                                    insuranceData={insuranceData}
                                />
                            </Box>
                        ) : null}

                        <Box sx={{ marginTop: '10px' }}>
                            <AddorEditInsurance
                                insuranceEntityTemplateId={
                                    insuranceEntityTemplateId
                                }
                                insuranceInstanceId={insuranceInstanceId}
                                shipmentInstanceId={shipmentInstanceId}
                                insuranceAttributesData={
                                    insuranceAttributesData
                                }
                                orgId={orgId}
                                readOnly={readOnly}
                                setReadOnly={setReadOnly}
                                reload={reload}
                            />
                        </Box>

                        <Box>
                            <InsuranceViewItems insuranceData={insuranceData} />
                        </Box>
                    </Box>

                    {/* Confirmation */}
                    <ConfirmationDialog
                        openDialog={isOpenUserActionSubmit}
                        handleComfirm={onSubmitUserActions}
                        handleClose={onCloseUserActionsSubmit}
                        DialogHeaderText={t('confirmAction')}
                        DialogBodyText={
                            <Typography>
                                {t('doYouWantTo')} <b>{userActionValue}</b>{' '}
                                {insuranceEntityTemplateName} ?
                            </Typography>
                        }
                        confirmtext={t('confirm')}
                        cancelText={t('cancel')}
                    />
                </>
            )}
        </Box>
    )
}

export default Insurance
